import { Pic } from "./Pic";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Recycle, Sun, Leaf } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const Ring = ({ icon: Icon }) => (
  <motion.svg viewBox="0 0 120 120" className="h-28 w-28 -rotate-90" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }}>
    <circle cx="60" cy="60" r="52" fill="none" stroke="#121110" strokeOpacity="0.12" strokeWidth="6" />
    <motion.circle
      cx="60" cy="60" r="52" fill="none" stroke="#5E8F2A" strokeWidth="6" strokeLinecap="round"
      variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1, transition: { duration: 1.8, ease: [0.22, 1, 0.36, 1] } } }}
    />
    <foreignObject x="35" y="35" width="50" height="50" className="rotate-90 [transform-origin:60px_60px]">
      <Icon className="h-[50px] w-[50px] p-2 text-[#3F6B12]" />
    </foreignObject>
  </motion.svg>
);

const PILLARS = [
  { id: "biomassa", icon: Recycle, value: "100%", title: "Scarti trasformati in biomassa", text: "Tutti gli scarti di lavorazione – rifili, segatura, trucioli – vengono recuperati e trasformati in biomassa. Nulla finisce in discarica." },
  { id: "fotovoltaico", icon: Sun, value: "Energia pulita", title: "Stabilimento a energia solare", text: "Il fabbisogno energetico della produzione è coperto da un impianto fotovoltaico: costruiamo con l'energia del sole." },
];

export const Sustainability = () => {
  const t = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="sostenibilita" ref={ref} className="bg-[#EAEFE3] px-5 py-28 text-[#121110] sm:px-8 lg:py-40" data-testid="sustainability-section">
      <div className="mx-auto max-w-[1500px]">
        <SectionLabel index="06">{t("sus.label", "Sostenibilità")}</SectionLabel>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
          <MaskLines
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-8 lg:text-[4.2rem]"
            lines={[t("sus.h1", "Niente si spreca."), <span key="s">{t("sus.h2a", "Tutto")} <em className="font-serif font-normal text-[#3F6B12]">{t("sus.h2em", "ritorna.")}</em></span>]}
          />
          <Reveal className="flex items-start gap-3 text-base leading-relaxed text-[#4D5246] sm:text-lg lg:col-span-4">
            <Leaf className="mt-1 h-5 w-5 shrink-0 text-[#3F6B12]" />
            {t("sus.text", "Il legno è un materiale rinnovabile che immagazzina CO₂. Noi chiudiamo il cerchio anche in fabbrica.")}
          </Reveal>
        </div>
        <div className="mt-16 grid gap-4 lg:grid-cols-12">
          <div className="relative h-[380px] overflow-hidden lg:col-span-5 lg:h-auto">
            <Pic as={motion.img} style={{ y }} src="/img/emma/sostenibilita.jpg" alt={t("sus.alt", "Copertura in legno lamellare contro il cielo")} className="absolute inset-0 h-[120%] w-full -translate-y-[10%] object-cover" />
          </div>
          <div className="grid gap-4 lg:col-span-7">
            {PILLARS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1} className="flex flex-col gap-8 bg-[#F7F5F0] p-8 sm:flex-row sm:items-center sm:p-10" data-testid={`sustainability-${p.id}`}>
                <Ring icon={p.icon} />
                <div>
                  <p className="font-display text-4xl font-extrabold tracking-tight text-[#3F6B12] sm:text-5xl" style={{ fontStretch: "80%" }}>{t(`sus.${p.id}.value`, p.value)}</p>
                  <h3 className="mt-2 font-display text-2xl font-semibold tracking-tight">{t(`sus.${p.id}.title`, p.title)}</h3>
                  <p className="mt-3 max-w-lg text-base leading-relaxed text-[#4D5246]">{t(`sus.${p.id}.text`, p.text)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
